import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart3, FileText, IndianRupee, Package, TrendingUp, Calendar } from 'lucide-react';

const Reports = () => {
  const [gstSummary, setGstSummary] = useState(null);
  const [receivables, setReceivables] = useState([]);
  const [inventory, setInventory] = useState(null);
  const [salesReport, setSalesReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const businessId = localStorage.getItem('currentBusiness');

  useEffect(() => { fetchReports(); }, []);

  const fetchReports = async () => {
    try {
      const [gstRes, receivablesRes, inventoryRes, salesRes] = await Promise.all([
        api.get(`/api/reports/${businessId}/gst-summary`),
        api.get(`/api/reports/${businessId}/receivables`),
        api.get(`/api/reports/${businessId}/inventory`),
        api.get(`/api/reports/${businessId}/sales`),
      ]);
      setGstSummary(gstRes.data);
      setReceivables(receivablesRes.data.receivables);
      setInventory(inventoryRes.data);
      setSalesReport(salesRes.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-bold font-heading flex items-center gap-3">
          <BarChart3 className="w-8 h-8" /> Reports
        </h1>
        <p className="text-sm text-gray-600 mt-1">Business insights and analytics</p>
      </div>

      <Tabs defaultValue="sales" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="sales" data-testid="sales-tab">Sales</TabsTrigger>
          <TabsTrigger value="gst" data-testid="gst-tab">GST</TabsTrigger>
          <TabsTrigger value="receivables" data-testid="receivables-tab">Receivables</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="inventory-tab">Inventory</TabsTrigger>
        </TabsList>

        {/* Sales Tab */}
        <TabsContent value="sales" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5" /> Monthly Sales</CardTitle>
            </CardHeader>
            <CardContent>
              {salesReport?.monthlySales?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3">Month</th>
                        <th className="text-right p-3">Invoices</th>
                        <th className="text-right p-3">Sales</th>
                        <th className="text-right p-3">Tax</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesReport.monthlySales.map((m) => (
                        <tr key={m.month} className="border-t" data-testid="monthly-sales-row">
                          <td className="p-3 font-medium">{m.month}</td>
                          <td className="text-right p-3">{m.invoiceCount}</td>
                          <td className="text-right p-3 font-semibold text-teal-700">{fmt(m.totalSales)}</td>
                          <td className="text-right p-3">{fmt(m.totalTax)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon={TrendingUp} message="No sales data yet. Create invoices to see sales reports." />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Package className="w-5 h-5" /> Top Items by Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              {salesReport?.topItems?.length > 0 ? (
                <div className="space-y-2">
                  {salesReport.topItems.map((item, i) => (
                    <div key={item.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid="top-item-row">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-gray-400 w-5">{i + 1}.</span>
                        <div>
                          <div className="font-medium text-sm">{item.name}</div>
                          <div className="text-xs text-gray-500">Qty sold: {item.totalQty}</div>
                        </div>
                      </div>
                      <span className="font-bold text-teal-700">{fmt(item.totalRevenue)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState icon={Package} message="No item data yet." />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GST Tab */}
        <TabsContent value="gst" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> GST Tax Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              {gstSummary?.gstBreakdown?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3">GST Rate</th>
                        <th className="text-right p-3">Taxable Amount</th>
                        <th className="text-right p-3">CGST</th>
                        <th className="text-right p-3">SGST</th>
                        <th className="text-right p-3">IGST</th>
                        <th className="text-right p-3">Total Tax</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gstSummary.gstBreakdown.map((item, index) => (
                        <tr key={index} className="border-t" data-testid="gst-breakdown-row">
                          <td className="p-3 font-medium">{item.rate}%</td>
                          <td className="text-right p-3">{fmt(item.taxable)}</td>
                          <td className="text-right p-3">{fmt(item.cgst)}</td>
                          <td className="text-right p-3">{fmt(item.sgst)}</td>
                          <td className="text-right p-3">{fmt(item.igst)}</td>
                          <td className="text-right p-3 font-semibold">{fmt(item.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon={FileText} message="No GST data yet. Create GST invoices to see the breakdown." />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>HSN-wise Summary</CardTitle></CardHeader>
            <CardContent>
              {gstSummary?.hsnSummary?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3">HSN Code</th>
                        <th className="text-right p-3">Taxable Value</th>
                        <th className="text-right p-3">GST Rate</th>
                        <th className="text-right p-3">Tax Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gstSummary.hsnSummary.map((item, index) => (
                        <tr key={index} className="border-t" data-testid="hsn-summary-row">
                          <td className="p-3 font-mono">{item.hsn}</td>
                          <td className="text-right p-3">{fmt(item.taxable)}</td>
                          <td className="text-right p-3">{item.rate}%</td>
                          <td className="text-right p-3 font-semibold">{fmt(item.tax)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon={FileText} message="No HSN data yet." />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Receivables Tab */}
        <TabsContent value="receivables" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><IndianRupee className="w-5 h-5" /> Outstanding Receivables</CardTitle>
            </CardHeader>
            <CardContent>
              {receivables.length > 0 ? (
                <div className="space-y-4">
                  {receivables.map((customer) => (
                    <div key={customer.customerId} className="border rounded-lg p-4" data-testid="receivable-customer-row">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold">{customer.customerName}</h3>
                          {customer.customerPhone && <p className="text-sm text-gray-600">{customer.customerPhone}</p>}
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-orange-600">{fmt(customer.totalDue)}</div>
                          <div className="text-xs text-gray-500">{customer.invoices.length} invoice(s)</div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {customer.invoices.map((inv) => (
                          <div key={inv.invoiceNo} className="flex justify-between text-sm bg-gray-50 p-2 rounded">
                            <div>
                              <span className="font-medium">{inv.invoiceNo}</span>
                              {inv.daysOverdue > 0 && <span className="ml-2 text-red-600 text-xs font-medium">({inv.daysOverdue} days overdue)</span>}
                            </div>
                            <span className="font-semibold">{fmt(inv.due)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState icon={IndianRupee} message="No outstanding receivables. All invoices are paid!" />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card data-testid="inventory-total-items">
              <CardHeader><CardTitle className="text-sm">Total Items</CardTitle></CardHeader>
              <CardContent><div className="text-3xl font-bold">{inventory?.totalItems || 0}</div></CardContent>
            </Card>
            <Card data-testid="inventory-valuation">
              <CardHeader><CardTitle className="text-sm">Total Valuation</CardTitle></CardHeader>
              <CardContent><div className="text-3xl font-bold text-teal-700">{fmt(inventory?.totalValuation)}</div></CardContent>
            </Card>
            <Card data-testid="inventory-low-stock-count">
              <CardHeader><CardTitle className="text-sm">Low Stock Items</CardTitle></CardHeader>
              <CardContent><div className="text-3xl font-bold text-red-600">{inventory?.lowStockItems?.length || 0}</div></CardContent>
            </Card>
          </div>

          {inventory?.lowStockItems && inventory.lowStockItems.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Package className="w-5 h-5" /> Low Stock Alert</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {inventory.lowStockItems.map((item) => (
                    <div key={item.id} className="flex justify-between items-center p-3 bg-red-50 rounded-lg" data-testid="low-stock-item-row">
                      <div>
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm text-gray-600">Current: {item.stock} {item.unit}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-red-600 font-medium">Alert at {item.lowStockAlert}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const EmptyState = ({ icon: Icon, message }) => (
  <div className="text-center py-8 text-gray-400">
    <Icon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
    <p className="text-sm">{message}</p>
  </div>
);

export default Reports;
